import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  cancelPaymentIntentService,
  createGuidePaymentIntentService,
  getGuideDetailService,
  getGuidePricePreviewService,
  getMyPurchasedGuidesService,
  getPaymentIntentStatusService,
  refundGuideService,
} from "../services/Guides2Service";
import {
  forgetPendingGuidePurchase,
  rememberPendingGuidePurchase,
} from "../storage/pendingGuidePurchase";
import { GUIDE_VARIANTS } from "../../shared/constants/guideVariants";

// VIVA-1214 — compra de UNA guía desde la liga de contratación.
//
// Orquesta el flujo completo que pide backend:
//   1. precio-preview (con o sin cupón) → desglose y total en USD.
//   2. create-payment-intent-generico → clientSecret para Stripe.js.
//   3. Stripe confirma en el navegador (GuidePaymentModal).
//   4. La guía la habilita el WEBHOOK, no el navegador (CA-09): aquí solo se
//      espera a que el intento o mis-guias-compradas digan que ya corrió.
//   5. Si el comprador abandona, se cancela el intento (CA-11).

// Reintentos mientras llega el webhook: 10 × 2 s. Si en 20 s no aparece, la
// compra no se pierde —el webhook la completa igual—; solo se avisa que la
// guía se habilitará en unos momentos.
export const ACCESS_POLL_ATTEMPTS = 10;
export const ACCESS_POLL_INTERVAL_MS = 2000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pickGuideId = (purchase) => purchase?.guideId ?? purchase?.GuideId;

const pick = (source, name) => {
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return source?.[name] ?? source?.[pascal];
};

const haveGuideId = (guideId) =>
  guideId !== undefined && guideId !== null && guideId !== "";

// ¿Ya está la guía entre las compras del usuario?
const guideIsPurchased = async ({ guideId, lang }) => {
  const result = await getMyPurchasedGuidesService({ lang });
  const purchases = Array.isArray(result?.data) ? result.data : [];
  return purchases.some((purchase) => String(pickGuideId(purchase)) === String(guideId));
};

// ¿El detalle de la guía ya da acceso a la variante de pago?
//
// Se pregunta porque mis-guias-compradas NO es la única fuente de la que depende
// la tarjeta: `obtener/detalle-guia` es quien resuelve `hasAccess` por variante y
// es la fuente que manda en `buildCard`. Las dos las escribe el mismo webhook,
// pero no tienen por qué quedar visibles a la vez, y cuando el acceso ya estaba
// dado y la compra todavía no figuraba en la lista, esta espera decía
// "pendiente" sobre una guía que el comprador ya podía leer, y la tarjeta se
// quedaba en "Ver precio" hasta recargar la página a mano.
const paidVariantIsReadable = async ({ guideId, lang }) => {
  const result = await getGuideDetailService({ guideId, lang });
  const rows = Array.isArray(result?.data) ? result.data : [];
  return rows.some(
    (row) =>
      pick(row, "guideVariantTypeCode") === GUIDE_VARIANTS.PAID &&
      pick(row, "hasAccess") === true
  );
};

// Las preguntas con las que se puede saber que el webhook ya habilitó la guía.
// Se hacen TODAS en cada vuelta y gana la primera que diga que sí.
//
// `payment-intent/{id}/status` (publicado el 25/09/2026) sería la vía directa,
// pero hoy no devuelve `hasAccess`; se deja preguntada para que el día que lo
// agregue la espera se vuelva inmediata sin tocar nada. Las otras dos son las
// mismas dos fuentes con las que la tarjeta decide si ofrece "Leer versión
// completa", así que cuando una responde que sí, la tarjeta va a cambiar.
//
// Al volver de un 3DS puede no saberse de qué guía era la compra; entonces la
// única vía es el estado del intento.
const accessQuestions = ({ paymentIntentId, guideId, lang }) => {
  const preguntas = [];

  if (paymentIntentId) {
    preguntas.push(
      async () => (await getPaymentIntentStatusService({ paymentIntentId }))?.hasAccess === true
    );
  }

  if (haveGuideId(guideId)) {
    preguntas.push(() => guideIsPurchased({ guideId, lang }));
    preguntas.push(() => paidVariantIsReadable({ guideId, lang }));
  }

  return preguntas;
};

// Reintenta hasta que alguna pregunta diga que sí o se acaben las vueltas. Un
// fallo de red en una vuelta no corta la espera: por eso `allSettled`.
const askUntilGranted = async ({ preguntas, attempts, intervalMs, isActive }) => {
  if (preguntas.length === 0) return false;

  for (let attempt = 0; attempt < attempts && isActive(); attempt += 1) {
    const respuestas = await Promise.allSettled(preguntas.map((preguntar) => preguntar()));
    if (respuestas.some((r) => r.status === "fulfilled" && r.value === true)) return true;

    if (attempt < attempts - 1) await wait(intervalMs);
  }
  return false;
};

// Espera a que la guía quede habilitada para el usuario. Devuelve true si lo
// quedó dentro de las vueltas dadas.
export const waitForPurchasedGuide = ({
  guideId,
  lang,
  attempts = ACCESS_POLL_ATTEMPTS,
  intervalMs = ACCESS_POLL_INTERVAL_MS,
  isActive = () => true,
}) =>
  askUntilGranted({
    preguntas: accessQuestions({ guideId, lang }),
    attempts,
    intervalMs,
    isActive,
  });

// CA-09, con el intento como testigo añadido a las dos fuentes de la tarjeta.
export const waitForPaymentAccess = ({
  paymentIntentId,
  guideId,
  lang,
  attempts = ACCESS_POLL_ATTEMPTS,
  intervalMs = ACCESS_POLL_INTERVAL_MS,
  isActive = () => true,
}) =>
  askUntilGranted({
    preguntas: accessQuestions({ paymentIntentId, guideId, lang }),
    attempts,
    intervalMs,
    isActive,
  });

// La misma operación reintentada debe viajar con la MISMA llave (regla de
// backend). "Misma operación" = misma guía, mismo cupón y mismo total
// esperado: si cambia cualquiera de los tres ya es otra compra y lleva llave
// nueva, porque reutilizar la anterior le pediría al servidor repetir algo que
// no es lo mismo.
const operationId = (guideId, couponCode, expectedAmountUsd) =>
  `${guideId}|${String(couponCode ?? "").trim().toUpperCase()}|${expectedAmountUsd ?? ""}`;

export const useGuidePurchase = ({ guideId, lang }) => {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [creating, setCreating] = useState(false);
  const keysRef = useRef(new Map());
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  const loadPreview = useCallback(
    async (couponCode = "") => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await getGuidePricePreviewService({ guideId, couponCode });
        if (!activeRef.current) return null;
        setPreview(result);
        // Un cupón rechazado no queda "aplicado": el total sigue sin él.
        setAppliedCoupon(result?.couponValid === true ? result.couponCode || couponCode : "");
        return result;
      } catch (error) {
        if (activeRef.current) setPreviewError(error);
        return null;
      } finally {
        if (activeRef.current) setPreviewLoading(false);
      }
    },
    [guideId]
  );

  useEffect(() => {
    loadPreview("");
  }, [loadPreview]);

  // Validar un cupón NO invalida el precio que ya se tenía: si la consulta con
  // cupón falla, el total sin cupón sigue siendo el bueno y se puede comprar
  // con él. Por eso el fallo se devuelve a quien aplicó el cupón en vez de
  // marcarse como error del desglose (visto en pre el 22/09/2026: con cualquier
  // cupón, precio-preview respondía 400 mientras sin cupón respondía 200).
  const applyCoupon = useCallback(
    async (couponCode) => {
      setPreviewLoading(true);
      try {
        const result = await getGuidePricePreviewService({ guideId, couponCode });
        if (!activeRef.current) return { preview: null, error: null };
        setPreview(result);
        setPreviewError(null);
        setAppliedCoupon(result?.couponValid === true ? result.couponCode || couponCode : "");
        return { preview: result, error: null };
      } catch (error) {
        return { preview: null, error };
      } finally {
        if (activeRef.current) setPreviewLoading(false);
      }
    },
    [guideId]
  );

  const removeCoupon = useCallback(() => loadPreview(""), [loadPreview]);

  // Crea (o recupera, si es un reintento) el PaymentIntent de esta compra.
  const startPurchase = useCallback(
    async ({ email, fullName }) => {
      const expectedAmountUsd = preview?.total;
      const opId = operationId(guideId, appliedCoupon, expectedAmountUsd);
      if (!keysRef.current.has(opId)) keysRef.current.set(opId, uuidv4());

      setCreating(true);
      try {
        const intent = await createGuidePaymentIntentService({
          guideId,
          email,
          fullName,
          idempotencyKey: keysRef.current.get(opId),
          couponCode: appliedCoupon,
          expectedAmountUsd,
        });

        // Si el banco pide 3DS, el navegador se va y vuelve con una carga
        // completa: este es el único rastro de qué guía se estaba comprando.
        rememberPendingGuidePurchase({
          paymentIntentId: intent?.paymentIntentId,
          guideId,
        });

        return { ...intent, operationId: opId };
      } catch (error) {
        // 409 "El monto no coincide": el precio cambió desde que se mostró. Se
        // vuelve a pedir el desglose para que el comprador vea el total real
        // antes de intentarlo otra vez (con otra llave, porque es otro total).
        if (error?.response?.status === 409) {
          keysRef.current.delete(opId);
          await loadPreview(appliedCoupon);
        }
        throw error;
      } finally {
        if (activeRef.current) setCreating(false);
      }
    },
    [guideId, appliedCoupon, preview, loadPreview]
  );

  // Operación terminada (pagada, cancelada o fallida) SIN salir del sitio: la
  // próxima compra es otra y lleva llave nueva, y el rastro para el retorno de
  // 3DS se retira —si se quedara, la siguiente carga de esta misma pestaña se
  // pondría a confirmar un pago que ya se resolvió aquí.
  const finishOperation = useCallback((opId) => {
    if (opId) keysRef.current.delete(opId);
    forgetPendingGuidePurchase();
  }, []);

  // CA-11: abandonar el pago no deja un cobro a medias. Si el intento ya no se
  // puede cancelar —porque se pagó o ya estaba cerrado— backend responde 404 y
  // no es un error para el comprador: la guía la resuelve el webhook.
  const cancelPurchase = useCallback(async ({ paymentIntentId, operationId: opId }) => {
    finishOperation(opId);
    if (!paymentIntentId) return;
    try {
      await cancelPaymentIntentService({ paymentIntentId });
    } catch {
      // Sin console.error: el repository ya dejó la falla en consola.
    }
  }, [finishOperation]);

  const waitForAccess = useCallback(
    ({ paymentIntentId } = {}) =>
      waitForPaymentAccess({
        paymentIntentId,
        guideId,
        lang,
        isActive: () => activeRef.current,
      }),
    [guideId, lang]
  );

  return {
    preview,
    previewLoading,
    previewError,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    creating,
    startPurchase,
    finishOperation,
    cancelPurchase,
    waitForAccess,
  };
};

// Reembolso de una guía comprada. SIN botón en la interfaz (decisión del
// 22/09/2026): la política de reembolso sigue en revisión legal (VIVA-1220).
// Queda lista para conectarse cuando exista esa política; misma regla de
// idempotencia: un reintento del mismo reembolso reutiliza su llave.
export const useGuideRefund = () => {
  const keysRef = useRef(new Map());
  const [refunding, setRefunding] = useState(false);

  const requestRefund = useCallback(async (guideId) => {
    const opId = String(guideId);
    if (!keysRef.current.has(opId)) keysRef.current.set(opId, uuidv4());

    setRefunding(true);
    try {
      const result = await refundGuideService({
        guideId,
        idempotencyKey: keysRef.current.get(opId),
      });
      keysRef.current.delete(opId);
      return result;
    } finally {
      setRefunding(false);
    }
  }, []);

  return { refunding, requestRefund };
};
